import React, { useState, useCallback } from 'react';
import { Building2, Home, Car, CheckCircle, MapPin, Gift, Search, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { api } from '../../App';
import { formatAddress } from '../../utils/format';

const LOCATION_OPTIONS = {
  salon: {
    icon: Building2,
    label: 'At their salon / studio',
    description: 'You visit the provider\'s location',
  },
  home: {
    icon: Home,
    label: 'At provider\'s home',
    description: 'Service at the provider\'s home-based studio',
  },
  mobile: {
    icon: Car,
    label: 'Mobile, they come to you',
    description: 'The provider travels to your location',
  },
};

function TravelChargeInfo({ provider }) {
  if (provider?.free_travel) {
    return (
      <div className="flex items-center gap-1 mt-1">
        <Gift className="h-3 w-3 text-green-600" />
        <span className="text-xs text-green-600 font-medium">Free travel</span>
      </div>
    );
  }
  const freeMiles = Number(provider?.free_travel_miles) || 0;
  const charges = provider?.travel_charges || [];
  if (charges.length === 0 && freeMiles === 0 && provider?.service_radius) {
    return <p className="text-xs text-stone-400 mt-0.5">Travels up to {provider.service_radius} miles</p>;
  }
  if (charges.length === 0 && freeMiles === 0) return null;
  const sorted = [...charges].sort((a, b) => a.max_miles - b.max_miles);
  return (
    <div className="mt-1 space-y-0.5">
      {freeMiles > 0 && (
        <p className="text-xs text-green-600 font-medium flex items-center gap-1">
          <Gift className="h-3 w-3" /> Free up to {freeMiles} {freeMiles === 1 ? 'mile' : 'miles'}
        </p>
      )}
      {sorted.map((tier) => (
        <p key={`tier-${tier.max_miles}`} className="text-xs text-stone-400">
          Up to {tier.max_miles} {tier.max_miles === 1 ? 'mile' : 'miles'}, £{Number(tier.charge).toFixed(2)}
        </p>
      ))}
    </div>
  );
}

function NearbyInRangeSuggestions({ provider, postcode }) {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!provider?.provider_id || !postcode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const category = provider.categories?.[0];
    const params = new URLSearchParams({ postcode, exclude: provider.provider_id, limit: '5' });
    if (category) params.set('category', category);
    api.get(`/providers/nearby-in-range?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setResults(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [provider?.provider_id, provider?.categories, postcode]);

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-stone-500" data-testid="nearby-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Finding providers who can travel to your postcode...</span>
      </div>
    );
  }
  if (error) return null;
  if (!results.length) {
    return (
      <p className="mt-3 text-xs text-stone-500" data-testid="nearby-empty">
        No other providers in this category travel to your postcode right now.
      </p>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-amber-200" data-testid="nearby-suggestions">
      <p className="text-xs font-medium text-amber-800 mb-2">
        Sorry, this provider doesn't travel here. Try one of these instead:
      </p>
      <div className="space-y-1.5">
        {results.map((r) => {
          const href = r.username ? `/@${r.username}` : `/provider/${r.provider_id}`;
          const feeLabel = r.free_travel || r.travel_fee === 0
            ? 'Free travel'
            : `£${Number(r.travel_fee).toFixed(2)} travel`;
          return (
            <a
              key={r.provider_id}
              href={href}
              className="flex items-center gap-3 p-2 rounded-md bg-white border border-amber-100 hover:border-[#D4AF37] transition-colors"
              data-testid={`nearby-provider-${r.provider_id}`}
            >
              {r.profile_image ? (
                <img
                  src={r.profile_image}
                  alt={r.brand_name || 'Provider'}
                  className="h-9 w-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4 text-stone-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">
                  {r.brand_name || 'Provider'}
                </p>
                <p className="text-[11px] text-stone-500 truncate">
                  {r.distance_miles} mi, {feeLabel}
                  {r.rating ? ` , ${Number(r.rating).toFixed(1)}` : ''}
                </p>
              </div>
              <span className="text-xs text-[#D4AF37] font-medium flex-shrink-0">View</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function PostcodeLookup({ provider, onQuoteResult }) {
  const [postcode, setPostcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const lookupPostcode = useCallback(async () => {
    if (!postcode.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get(`/providers/${provider.provider_id}/travel-quote?postcode=${encodeURIComponent(postcode.trim())}`);
      setResult(res.data);
      onQuoteResult(res.data, postcode.trim());
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not calculate travel fee';
      setError(msg);
      onQuoteResult(null, null);
    } finally {
      setLoading(false);
    }
  }, [postcode, provider.provider_id, onQuoteResult]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      lookupPostcode();
    }
  };

  return (
    <div className="mt-3 space-y-2" data-testid="postcode-lookup">
      <p className="text-xs text-stone-500 font-medium">Enter your postcode for an accurate travel fee</p>
      <div className="flex gap-2">
        <Input
          value={postcode}
          onChange={(e) => setPostcode(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          placeholder="e.g. SW2 1JQ"
          className="input-zuriya text-sm font-mono uppercase flex-1"
          data-testid="client-postcode-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={lookupPostcode}
          disabled={!postcode.trim() || loading}
          className="shrink-0"
          data-testid="lookup-postcode-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="postcode-error">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && result.within_radius && result.fee !== null && !result.free_travel && (
        <div className="bg-green-50 border border-green-200 p-3 rounded-lg" data-testid="postcode-result">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">
                {result.distance_miles} miles away
              </p>
              <p className="text-xs text-green-600">
                {result.tier
                  ? `Within ${result.tier.max_miles}-mile zone`
                  : 'Within travel radius'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-green-700" data-testid="calculated-travel-fee">
                {result.fee === 0 ? 'Free' : `£${result.fee.toFixed(2)}`}
              </p>
              <p className="text-xs text-green-500">travel fee</p>
            </div>
          </div>
          {result.breakdown && (
            <p
              className="text-[11px] text-green-700/80 mt-2 leading-snug"
              data-testid="travel-breakdown"
            >
              {result.breakdown}
            </p>
          )}
        </div>
      )}

      {result && !result.within_radius && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg" data-testid="postcode-out-of-range">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                {result.distance_miles} miles, outside travel range
              </p>
              <p className="text-xs text-amber-600">
                This provider travels up to {result.max_distance} miles. Try a closer postcode or choose a different service location.
              </p>
            </div>
          </div>
          <NearbyInRangeSuggestions provider={provider} postcode={postcode.trim()} />
        </div>
      )}

      {result && result.free_travel && (
        <div className="bg-green-50 border border-green-200 p-3 rounded-lg" data-testid="postcode-free-travel">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-green-600" />
            <p className="text-sm text-green-700 font-medium">Free travel, no extra charge</p>
          </div>
          {result.breakdown && (
            <p
              className="text-[11px] text-green-700/80 mt-1 leading-snug"
              data-testid="travel-breakdown-free"
            >
              {result.breakdown}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function ServiceLocationPicker({ serviceTypes, selected, onSelect, provider, onTravelQuote }) {
  if (!serviceTypes || serviceTypes.length === 0) return null;

  const handleQuoteResult = (result, postcode) => {
    if (onTravelQuote) {
      onTravelQuote(result, postcode);
    }
  };

  const showPostcodeLookup = (type) => {
    if (type !== 'mobile' || selected !== 'mobile' || !provider) return false;
    if (provider.free_travel) return false;
    const hasTiers = (provider.travel_charges?.length || 0) > 0;
    const hasFreeZone = Number(provider.free_travel_miles || 0) > 0;
    const hasRadius = Number(provider.service_radius || 0) > 0;
    // Show the lookup whenever the provider set *any* travel boundary so the
    // client can enter a postcode and we can validate in-range before checkout.
    return hasTiers || hasFreeZone || hasRadius;
  };

  // If only one service type, auto-select it and show info only
  if (serviceTypes.length === 1) {
    const type = serviceTypes[0];
    const opt = LOCATION_OPTIONS[type];
    if (!opt) return null;

    // Auto-select on first render
    if (!selected) {
      setTimeout(() => onSelect(type), 0);
    }

    return (
      <div className="mb-6" data-testid="service-location-section">
        <h3 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#D4AF37]" />
          Service Location
        </h3>
        <div className="p-4 bg-[#F9F6F0] rounded-lg border border-[#E6E4DD]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-white flex items-center justify-center flex-shrink-0">
              <opt.icon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-[#1A1A1A] text-sm">{opt.label}</p>
              {type === 'salon' && provider?.salon_address && (
                <p className="text-xs text-stone-500 mt-0.5">{formatAddress(provider.salon_address)}</p>
              )}
              {type === 'mobile' && <TravelChargeInfo provider={provider} />}
            </div>
          </div>
          {showPostcodeLookup(type) && (
            <PostcodeLookup provider={provider} onQuoteResult={handleQuoteResult} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="service-location-section">
      <h3 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-[#D4AF37]" />
        Where would you like your service?
      </h3>
      <div className="space-y-2">
        {serviceTypes.map((type) => {
          const opt = LOCATION_OPTIONS[type];
          if (!opt) return null;
          const isSelected = selected === type;
          return (
            <div key={type}>
              <button
                onClick={() => onSelect(type)}
                className={`w-full p-4 rounded-lg border text-left transition-all flex items-center gap-4 ${
                  isSelected
                    ? 'border-[#D4AF37] bg-[#F9F6F0] ring-2 ring-[#D4AF37]'
                    : 'border-[#E6E4DD] hover:border-[#D4AF37]/50'
                }`}
                data-testid={`location-option-${type}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[#D4AF37] text-white' : 'bg-[#F9F6F0] text-[#D4AF37]'
                }`}>
                  <opt.icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${isSelected ? 'text-[#1A1A1A]' : 'text-stone-700'}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-stone-500">{opt.description}</p>
                  {type === 'salon' && provider?.salon_address && (
                    <p className="text-xs text-stone-400 mt-0.5 truncate">{formatAddress(provider.salon_address)}</p>
                  )}
                  {type === 'mobile' && <TravelChargeInfo provider={provider} />}
                </div>
                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                )}
              </button>
              {showPostcodeLookup(type) && (
                <div className="ml-14 mr-4 mt-2">
                  <PostcodeLookup provider={provider} onQuoteResult={handleQuoteResult} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!selected && (
        <p className="text-xs text-red-500 mt-2">Please select a service location to continue</p>
      )}
    </div>
  );
}
